import { Component, EventEmitter, inject, Input, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MeterDetailModel } from 'src/app/models/maintenance/meter-detail.model';
import { MeterModel } from 'src/app/models/maintenance/meter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { MeterDetailService } from 'src/app/services/maintenance/meter-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-meter-detail',
  templateUrl: './register-meter-detail.component.html',
  styleUrl: './register-meter-detail.component.scss',
  standalone: false,
})
export class RegisterMeterDetailComponent {

  private readonly articleService = inject(ArticleService);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly meterDetailService = inject(MeterDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @Input() meterDetailContract: MeterDetailModel = new MeterDetailModel();
  @Input() meterModel: MeterModel = new MeterModel();
  @Input() meterDetails: MeterDetailModel[] = [];
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() onResetHourMeter = new EventEmitter<MeterDetailModel>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  codeNameArticle: string = '';
  formMeterDetail: FormGroup = new FormGroup({});
  meterDetailId: number = 0;
  listHourMeterResetOptions: { label: string, value: boolean }[] = [
    { label: 'SI', value: true },
    { label: 'NO', value: false }
  ];

  async ngOnChanges(changes: SimpleChanges) {
    this.validateForm(new MeterDetailModel());
    const { meterDetails, meterDetailContract } = changes;
    if (meterDetails?.firstChange === false) {
      await this.createMeterDetails(this.meterDetails);
      return;
    }
    if (meterDetailContract?.firstChange === false) {
      await this.getArticle(this.meterDetailContract.articleId);
      if (this.meterDetailContract.meterDetailId === 0) {
        this.createNewDetail();
        return;
      }
      this.validateForm(this.meterDetailContract);
      this.meterDetailId = this.meterDetailContract.meterDetailId;
    }
  }

  clearForm() {
    this.meterDetailId = 0;
    this.codeNameArticle = '';
    this.isFormView.emit(false);
    this.validateForm(new MeterDetailModel());
  }

  createNewDetail() {
    this.meterDetailId = 0;
    this.validateForm(this.meterDetailContract);
  }

  async createMeterDetails(meterDetails: MeterDetailModel[]) {
    try {
      await firstValueFrom(
        this.meterDetailService.createDetails(meterDetails)
      );
      this.toastHandlerService.showToast(
        'success',
        'Los detalles del traslado fueron creados exitosamente'
      );
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles del traslado.', error);
    }
  }

  async createMeterDetail(meterDetailModel: MeterDetailModel) {
    try {
      await firstValueFrom(
        this.meterDetailService.create(meterDetailModel)
      );
      this.toastHandlerService.showToast(
        'success',
        'El detalle del traslado fue creado exitosamente.'
      );
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al crear el detalle del traslado.', error);
    }
  }

  async getArticle(articleId: number) {
    try {
      const article = await firstValueFrom(
        this.articleService.getArticleById(articleId)
      );
      this.codeNameArticle = `${article.code} - ${article.name}`;
    } catch (error) {
      console.error('Error al obtener el articulo por Id.', error);
    }
  }

  async onChangeResetHourMeter() {
    const model: MeterDetailModel = {
      ...this.meterDetailContract,
      ...this.formMeterDetail.getRawValue() as MeterDetailModel,
      isResetHourMeter: this.meterDetailContract.isResetHourMeter,
    };
    try {
      const response = await firstValueFrom(
        this.meterDetailService.calculateResetHourMeter(model)
      );
      this.meterDetailContract = response;
      this.validateForm(this.meterDetailContract);
    } catch (error) {
      this.formMeterDetail.patchValue({ isResetHourMeter: this.meterDetailContract.isResetHourMeter });
      console.error('Error al calcular el reinicio del medidor.', error);
    }
  }

  async onCaptureQuantityChange(value: number) {
    try {
      this.formMeterDetail.patchValue({ captureQuantity: value });
      const model = this.formMeterDetail.getRawValue() as MeterDetailModel;
      model.meterId = this.meterModel.meterId;
      const quantity = await firstValueFrom(
        this.meterDetailService.getCalculatedQuantity(model)
      );
      this.formMeterDetail.patchValue({ quantity });
    } catch (error) {
      console.error('Error al calcular la cantidad.', error);
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formMeterDetail.valid) {
      this.showValidationMessagesDetail();
      return;
    }
    const model = this.formMeterDetail.getRawValue() as MeterDetailModel;
    model.meterId = this.meterModel.meterId;
    if (this.meterDetailId == 0) {
      await this.createMeterDetail(model);
    } else {
      await this.updateMeterDetail(model);
    }
  }

  showValidationMessagesDetail() {
    const controlMessages: Record<string, Record<string, string>> = {
      articleId: {
        required: 'Debe seleccionar un Artículo.',
        min: 'Debe seleccionar un Artículo válido.'
      },
      dateMeterDetail: {
        required: 'Debe ingresar la Fecha del Detalle.'
      },
      isResetHourMeter: {
        required: 'Debe indicar si se resetea el horómetro.'
      },
      quantity: {
        required: 'Debe ingresar una Cantidad.',
        min: 'La Cantidad debe ser mayor o igual a 0.'
      },
      captureQuantity: {
        required: 'Debe ingresar una Cantidad de Captura.',
        min: 'La Cantidad de Captura debe ser mayor o igual a 0.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formMeterDetail.get(controlName);
      if (control?.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateMeterDetail(modelMeterDetail: MeterDetailModel) {
    try {
      this.meterDetailContract.quantity = modelMeterDetail.quantity;
      this.meterDetailContract.captureQuantity = modelMeterDetail.captureQuantity;
      this.meterDetailContract.isResetHourMeter = modelMeterDetail.isResetHourMeter;
      this.meterDetailContract.dateMeterDetail = modelMeterDetail.dateMeterDetail;
      await firstValueFrom(
        this.meterDetailService.update(this.meterDetailContract)
      );
      this.toastHandlerService.showToast(
        'success',
        `El detalle del medidor fue modificado exitosamente.`
      );
      this.updateListDetail.emit(true);
      this.clearForm();
    } catch (error) {
      console.error('Error al modificar el detalle del medidor.', error);
    }
  }

  validateForm(meterDetailModel: MeterDetailModel) {
    this.formMeterDetail = this.formBuilder.group({
      articleId: [meterDetailModel.articleId, [Validators.required, Validators.min(1)]],
      dateMeterDetail: [meterDetailModel.dateMeterDetail
        ? this.dateConverterService.convertUtcToLocalDate(meterDetailModel.dateMeterDetail)
        : null, [Validators.required]],
      isResetHourMeter: [meterDetailModel.isResetHourMeter, [Validators.required]],
      quantity: [meterDetailModel.quantity, [Validators.required, Validators.min(0)]],
      captureQuantity: [meterDetailModel.captureQuantity, [Validators.required, Validators.min(0)]]
    });
    this.formMeterDetail.controls['quantity'].disable();
  }

  validateFormControls() {
    Object.keys(this.formMeterDetail.controls).forEach((key) => {
      const control = this.formMeterDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
